import { Grid, Typography } from '@mui/material';
import { memo } from 'react';

import {
  convertBuildingStandard,
  convertEstimatedAge,
  convertHouseLotPropertyUse,
  convertImplantationInsertion,
  convertInspectionType,
  convertInternalWall,
  convertLotPlacement,
  convertOccupancyStatus,
  convertPreservationState,
  convertSolarOrientation,
  convertViewQuality,
} from '../../../api/characteristics/enumerations';
import {
  HouseEvaluatingUnit,
  SectionStatus,
} from '../../../api/characteristics/types';
import { formatDate } from '../../../helpers';
import { SectionStatusBadge } from '../components/StatusIndicators';
import { StyledDivider } from '../styles';

interface HouseEvaluatingUnitSectionProps {
  formData: HouseEvaluatingUnit;
  sectionStatus?: SectionStatus;
}

export const HouseEvaluatingUnitSection = memo(
  function HouseEvaluatingUnitSection({
    formData,
    sectionStatus,
  }: HouseEvaluatingUnitSectionProps) {
    return (
      <Grid container spacing={2} marginBottom={3}>
        <Grid item xs={12}>
          <SectionStatusBadge status={sectionStatus} />
        </Grid>
        <Grid item xs={12}>
          <Typography
            fontSize={18}
            fontWeight={600}
            id="evaluating-unit-vistoria"
          >
            Vistoria
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Vistoria
              </Typography>
              <Typography>
                {formData.inspection.inspection_type
                  ? convertInspectionType(formData.inspection.inspection_type)
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Data da Vistoria
              </Typography>
              <Typography>
                {(formData.inspection.inspection_date &&
                  formatDate(formData.inspection.inspection_date)) ||
                  '-'}
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <StyledDivider />
        </Grid>
        <Grid item xs={12}>
          <Typography
            fontSize={18}
            fontWeight={600}
            id="evaluating-unit-caracteristicas-gerais"
          >
            Características Gerais
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Estado de Conservação
              </Typography>
              <Typography>
                {formData.general_characteristics.preservation_state
                  ? convertPreservationState(
                      formData.general_characteristics.preservation_state
                    )
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Padrão de Acabamento
              </Typography>
              <Typography>
                {formData.general_characteristics.building_standard
                  ? convertBuildingStandard(
                      formData.general_characteristics.building_standard
                    )
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Paredes Externas
              </Typography>
              <Typography>
                {formData.general_characteristics.external_wall
                  ? convertInternalWall(
                      formData.general_characteristics.external_wall
                    )
                  : '-'}
              </Typography>
            </Grid>
            {formData.general_characteristics.external_wall_other && (
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">
                  Paredes Externas - Outros
                </Typography>
                <Typography>
                  {formData.general_characteristics.external_wall_other}
                </Typography>
              </Grid>
            )}
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Idade Estimada
              </Typography>
              <Typography>
                {formData.general_characteristics.estimated_age
                  ? convertEstimatedAge(
                      formData.general_characteristics.estimated_age
                    )
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Destinação
              </Typography>
              <Typography>
                {formData.general_characteristics.property_use
                  ? convertHouseLotPropertyUse(
                      formData.general_characteristics.property_use
                    )
                  : '-'}
              </Typography>
            </Grid>
            {formData.general_characteristics.property_use_other && (
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">
                  Destinação - Outros
                </Typography>
                <Typography>
                  {formData.general_characteristics.property_use_other}
                </Typography>
              </Grid>
            )}
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Ocupação
              </Typography>
              <Typography>
                {formData.general_characteristics.occupancy_status
                  ? convertOccupancyStatus(
                      formData.general_characteristics.occupancy_status
                    )
                  : '-'}
              </Typography>
            </Grid>
            {formData.general_characteristics.occupancy_status_other && (
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">
                  Ocupação - Outros
                </Typography>
                <Typography>
                  {formData.general_characteristics.occupancy_status_other}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <StyledDivider />
        </Grid>
        <Grid item xs={12}>
          <Typography
            fontSize={18}
            fontWeight={600}
            id="evaluating-unit-posicao-insercao"
          >
            Posição e Inserção
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Orientação Solar
              </Typography>
              <Typography>
                {formData.position_and_insertion.solar_orientation
                  ? convertSolarOrientation(
                      formData.position_and_insertion.solar_orientation
                    )
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Vista
              </Typography>
              <Typography>
                {formData.position_and_insertion.view_quality
                  ? convertViewQuality(
                      formData.position_and_insertion.view_quality
                    )
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Implantação/Inserção
              </Typography>
              <Typography>
                {formData.position_and_insertion.implantation_insertion
                  ? convertImplantationInsertion(
                      formData.position_and_insertion.implantation_insertion
                    )
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Locação do Terreno
              </Typography>
              <Typography>
                {formData.position_and_insertion.lot_placement
                  ? convertLotPlacement(
                      formData.position_and_insertion.lot_placement
                    )
                  : '-'}
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <StyledDivider />
        </Grid>
        <Grid item xs={12}>
          <Typography
            fontSize={18}
            fontWeight={600}
            id="evaluating-unit-vagas-estacionamento"
          >
            Vagas
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Total de Vagas Cobertas
              </Typography>
              <Typography>{formData.parking_spaces.covered ?? '-'}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Total de Vagas Descobertas
              </Typography>
              <Typography>
                {formData.parking_spaces.uncovered ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Total de Vagas
              </Typography>
              <Typography>{formData.parking_spaces.total ?? '-'}</Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <StyledDivider />
        </Grid>
        <Grid item xs={12}>
          <Typography
            fontSize={18}
            fontWeight={600}
            id="evaluating-unit-pavimentos"
          >
            Pavimentos
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Qtd de Pavimentos Unidade
              </Typography>
              <Typography>{formData.floors.total ?? '-'}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                N° Pavimentos Subsolos
              </Typography>
              <Typography>{formData.floors.basements ?? '-'}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                N° Pavimentos Térreos
              </Typography>
              <Typography>{formData.floors.ground_floors ?? '-'}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                N° Pavimentos Mezaninos
              </Typography>
              <Typography>{formData.floors.mezzanines ?? '-'}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                N° Pavimentos Sobrelajes
              </Typography>
              <Typography>{formData.floors.slabs ?? '-'}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                N° Pavimentos Coberturas
              </Typography>
              <Typography>{formData.floors.penthouses ?? '-'}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                N° Pavimentos Outros
              </Typography>
              <Typography>{formData.floors.others ?? '-'}</Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }
);
